/* eslint-disable react/no-array-index-key */
import {
  formulierPageContainerClassName,
  formulierPageContainerStepperFormClassName
} from "@opencak-klantondersteuning/shared";
import { BetalingsregelingAanvragenPage } from "@page/betalingsregeling-aanvragen";
import { AvgFormulierPage } from "@page/forms-avgformulier";
import { BewindvoerdersCuratorenClientAanmeldenFormulierPage } from "@page/forms-bewindvoerders-curatoren-client-aanmelden-formulier";
import { BewindvoerdersCuratorenClientWijzigenFormulierPage } from "@page/forms-bewindvoerders-curatoren-client-wijzigen-formulier";
import { ContactOpnemenFormulierPage } from "@page/forms-contactformulier";
import { KlachtFormulierPage } from "@page/forms-klachtenformulier";
import { OnderhoudPage } from "@page/onderhoud";
import { Language, setCurrentLanguage } from "@shared/locales";
import axios from "axios";
import { PiletApi } from "opencak-app";
import * as React from "react";
import { interceptors } from "./interceptors/Interceptors";

export function setup(app: PiletApi) {
  const basePath = app.getData("base-path");
  const apiUrl = app.getData("api-path");
  const authEnabled = app.getData("api-auth-enabled");
  const Layout = app.getFormPageLayoutComponent();

  const allFeatures = app.allFeaturesToggle();
  const onderhoudEnabled = allFeatures.find((feature) => feature.name === "formulieren/onderhoud")?.enabled;

  const lang = app.getLanguage();
  const onderhoudPagePath =
    lang === Language.Nederlands ? `${basePath}/onderhoud` : `${basePath}/maintenance`;

  if (onderhoudEnabled) {
    const onderhoudPage = () => <OnderhoudPage language={lang} />;
    app.registerPage(onderhoudPagePath, onderhoudPage);

    if (window.location.pathname !== onderhoudPagePath) {
      window.location.assign(onderhoudPagePath);
    }
    return;
  }

  const onderhoudPagePathRegex = new RegExp(`^${onderhoudPagePath}/?$`);
  const isOnderhoudPage = onderhoudPagePathRegex.test(window.location.pathname);
  if (isOnderhoudPage) {
    window.location.assign("/");
    return;
  }

  const betalingsRegelingPath = "/zelf-regelen/betalingsregeling-wmo-2020";
  const BetalingsregelingForm = () => (
    <Layout>
      <div className={`${formulierPageContainerClassName}`}>
        <BetalingsregelingAanvragenPage pageBaseUrl={`${basePath}${betalingsRegelingPath}`} apiUrl={apiUrl} />
      </div>
    </Layout>
  );
  app.registerPage(`${basePath}${betalingsRegelingPath}/:formStep?`, BetalingsregelingForm);

  const klachtenFormulierNlPath = "/zelf-regelen/klachtenformulier";
  const klachtenFormulierEnPath = "/about/contact/complaint-form/en";
  const KlachtenFormulierForm = (currentLanguage: string) => () => {
    React.useEffect(() => {
      interceptors.initialize(axios, currentLanguage);
    }, []);

    setCurrentLanguage(currentLanguage === "nl" ? Language.Nederlands : Language.English);
    return (
      <Layout>
        <div className={`${formulierPageContainerStepperFormClassName}`}>
          <KlachtFormulierPage
            basePath={basePath}
            pageBaseUrl={currentLanguage === "nl" ? klachtenFormulierNlPath : klachtenFormulierEnPath}
            apiUrl={apiUrl}
            language={currentLanguage === "nl" ? Language.Nederlands : Language.English}
          />
        </div>
      </Layout>
    );
  };

  app.registerPage(`${basePath}${klachtenFormulierNlPath}/:formStep?`, KlachtenFormulierForm("nl"));
  app.registerPage(`${basePath}${klachtenFormulierEnPath}/:formStep?`, KlachtenFormulierForm("en"));

  const contactFormulierNlPath = "/over/contact/contactformulier";
  const contactFormulierEnPath = "/about/contact/contact-form/en";
  const ContactFormulierFormCMS = (currentLanguage: string) => () => {
    React.useEffect(() => {
      interceptors.initialize(axios, currentLanguage);
    }, []);

    setCurrentLanguage(currentLanguage === "nl" ? Language.Nederlands : Language.English);
    return (
      <Layout>
        <div className="formulier-page-container-stepper-form">
          <ContactOpnemenFormulierPage
            basePath={basePath}
            pageBaseUrl={currentLanguage === "nl" ? contactFormulierNlPath : contactFormulierEnPath}
            apiUrl={apiUrl}
            language={currentLanguage === "nl" ? Language.Nederlands : Language.English}
          />
        </div>
      </Layout>
    );
  };

  app.registerPage(`${basePath}${contactFormulierNlPath}/:formStep?`, ContactFormulierFormCMS("nl"));
  app.registerPage(`${basePath}${contactFormulierEnPath}/:formStep?`, ContactFormulierFormCMS("en"));

  const bewindvoerdersCuratorenClientAanmeldenNlPath = "/over/contact/bewindvoerders-en-curatoren";
  const bewindvoerdersCuratorenClientAanmeldenEnPath = "/about/contact/bewindvoerders-en-curatoren/en";
  const BewindvoerdersCuratorenClientAanmeldenFormCMS = (currentLanguage: string) => () => {
    React.useEffect(() => {
      interceptors.initialize(axios, currentLanguage);
    }, []);

    setCurrentLanguage(currentLanguage === "nl" ? Language.Nederlands : Language.English);
    return (
      <Layout>
        <div className="formulier-page-container-stepper-form">
          <BewindvoerdersCuratorenClientAanmeldenFormulierPage
            basePath={basePath}
            pageBaseUrl={
              currentLanguage === "nl"
                ? bewindvoerdersCuratorenClientAanmeldenNlPath
                : bewindvoerdersCuratorenClientAanmeldenEnPath
            }
            apiUrl={apiUrl}
            language={currentLanguage === "nl" ? Language.Nederlands : Language.English}
          />
        </div>
      </Layout>
    );
  };

  app.registerPage(
    `${basePath}${bewindvoerdersCuratorenClientAanmeldenNlPath}/:formStep?`,
    BewindvoerdersCuratorenClientAanmeldenFormCMS("nl")
  );
  app.registerPage(
    `${basePath}${bewindvoerdersCuratorenClientAanmeldenEnPath}/:formStep?`,
    BewindvoerdersCuratorenClientAanmeldenFormCMS("en")
  );

  const bewindvoerdersCuratorenClientWijzigenNlPath = "/over/contact/bewindvoerders-en-curatoren-wijzigen";
  const bewindvoerdersCuratorenClientWijzigenEnPath =
    "/about/contact/bewindvoerders-en-curatoren-wijzigen/en";
  const BewindvoerdersCuratorenClientWijzigenFormCMS = (currentLanguage: string) => () => {
    React.useEffect(() => {
      interceptors.initialize(axios, currentLanguage);
    }, []);

    setCurrentLanguage(currentLanguage === "nl" ? Language.Nederlands : Language.English);
    return (
      <Layout>
        <div className="formulier-page-container-stepper-form">
          <BewindvoerdersCuratorenClientWijzigenFormulierPage
            basePath={basePath}
            pageBaseUrl={
              currentLanguage === "nl"
                ? bewindvoerdersCuratorenClientWijzigenNlPath
                : bewindvoerdersCuratorenClientWijzigenEnPath
            }
            apiUrl={apiUrl}
            language={currentLanguage === "nl" ? Language.Nederlands : Language.English}
          />
        </div>
      </Layout>
    );
  };

  app.registerPage(
    `${basePath}${bewindvoerdersCuratorenClientWijzigenNlPath}/:formStep?`,
    BewindvoerdersCuratorenClientWijzigenFormCMS("nl")
  );
  app.registerPage(
    `${basePath}${bewindvoerdersCuratorenClientWijzigenEnPath}/:formStep?`,
    BewindvoerdersCuratorenClientWijzigenFormCMS("en")
  );

  const avgFormulierNlPath = "/zelf-regelen/avgformulier";
  const avgFormulierEnPath = "/about/contact/avg-form/en";
  const AvgFormulierForm = (currentLanguage: string) => () => {
    React.useEffect(() => {
      interceptors.initialize(axios, currentLanguage);
    }, []);

    setCurrentLanguage(currentLanguage === "nl" ? Language.Nederlands : Language.English);
    return (
      <Layout>
        <div className="formulier-page-container-stepper-form">
          <AvgFormulierPage
            basePath={basePath}
            pageBaseUrl={currentLanguage === "nl" ? avgFormulierNlPath : avgFormulierEnPath}
            apiUrl={apiUrl}
            language={currentLanguage === "nl" ? Language.Nederlands : Language.English}
          />
        </div>
      </Layout>
    );
  };

  app.registerPage(`${basePath}${avgFormulierNlPath}/:formStep?`, AvgFormulierForm("nl"));
  app.registerPage(`${basePath}${avgFormulierEnPath}/:formStep?`, AvgFormulierForm("en"));

  axios.defaults.withCredentials = authEnabled;
}
